import * as React from 'react'

import { Control, FieldPath, FieldValues, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Checkbox,
  CheckboxGroup,
  Flex,
  Grid,
  Label,
  RadioButton,
  Stack,
  Text,
} from '@enechain/ecloud-designsystem'
import { ProductSpreadType } from '@enechain/eposition-proto/types/v1/deal_pb'
import {
  Card,
  DEAL_DELIVERY_TYPE,
  FormNumberInputWithUnit,
} from '@enechain/eposition-ui'

import { useSpreadTypeOption } from './useSpreadTypeOption'

type Props<T extends FieldValues> = {
  schemaKeyName: FieldPath<T>
  control: Control<T>
  hiddenDiffUnitPrice?: boolean
}

export const SpreadTypeForm = <T extends FieldValues>({
  schemaKeyName,
  control,
  hiddenDiffUnitPrice = false,
}: Props<T>): React.ReactElement => {
  const { t } = useTranslation(['domain', 'common'])

  const { productSpreadTypes, enabledOptions } = useSpreadTypeOption()

  const { field: deliveryTypeField } = useController({
    name: 'deliveryType' as FieldPath<T>,
    control,
  })

  const { field: isSpreadTradeField } = useController({
    name: `${schemaKeyName}.isSpreadTrade` as FieldPath<T>,
    control,
  })

  const { field: spreadTypeField } = useController({
    name: `${schemaKeyName}.spreadType` as FieldPath<T>,
    control,
  })

  return (
    <Card>
      <Stack gap="4">
        <Grid gridGap="4" gridTemplateColumns="auto 1fr">
          <Label required>
            {t('domain:model.posting.column.spread_trade')}
          </Label>
          <RadioButton
            onValueChange={({ value }): void => {
              const newValue = value === 'true'
              isSpreadTradeField.onChange(newValue)
              if (newValue) {
                spreadTypeField.onChange([ProductSpreadType.AREA])
              }
            }}
            disabled={deliveryTypeField.value === DEAL_DELIVERY_TYPE.JEPX_BG}
            value={isSpreadTradeField.value === true ? 'true' : 'false'}
            radios={enabledOptions}
          />
        </Grid>
        {isSpreadTradeField.value === true && (
          <Card backgroundColor="Surface.secondary">
            <Grid gridGap="4" gridTemplateColumns="auto 1fr">
              <Label required>
                {t('domain:model.posting.column.spread_type')}
              </Label>
              <CheckboxGroup
                onValueChange={(value) =>
                  spreadTypeField.onChange(value.map((item) => Number(item)))
                }
                value={spreadTypeField.value.map((item: ProductSpreadType) =>
                  item.toString(),
                )}
              >
                <Flex gap="2" height="full">
                  {productSpreadTypes.map((status) => (
                    <Checkbox key={status.value} value={status.value}>
                      <Text whiteSpace="nowrap">{status.label}</Text>
                    </Checkbox>
                  ))}
                </Flex>
              </CheckboxGroup>
              {!hiddenDiffUnitPrice && (
                <>
                  <Label>{t('domain:shared.unit_price.difference')}</Label>
                  <FormNumberInputWithUnit
                    unit={t('common:unit.yen_per_kwh')}
                    name={`${schemaKeyName}.diffUnitPrice` as FieldPath<T>}
                    control={control}
                  />
                </>
              )}
            </Grid>
          </Card>
        )}
      </Stack>
    </Card>
  )
}
